
const OSRM_API_BASE = 'https://router.project-osrm.org/route/v1/driving';

interface OSRMResponse {
    code: string;
    routes: {
        distance: number; // Meters
        duration: number; // Seconds
        geometry?: any; // GeoJSON geometry
    }[];
}

const CACHE_KEY_PREFIX = 'OSRM_ROUTE_v1_';

/**
 * v2026.02.12: Extended to return geometry for route visualization
 */
export const getOsrmRoute = async (
    lat1: number, lng1: number,
    lat2: number, lng2: number,
    includeGeometry = false
): Promise<{ distanceKm: number, durationMins: number, geometry?: any } | null> => {

    // 1. Check Cache (Simple SessionStorage or LRU conceptual)
    const key = `${CACHE_KEY_PREFIX}${lat1.toFixed(4)},${lng1.toFixed(4)}_${lat2.toFixed(4)},${lng2.toFixed(4)}`;
    const cached = sessionStorage.getItem(key);
    if (cached) {
        return JSON.parse(cached);
    }

    try {
        // 2. Query OSRM
        // Format: /lon,lat;lon,lat
        const overview = includeGeometry ? 'full' : 'false';
        const url = `${OSRM_API_BASE}/${lng1},${lat1};${lng2},${lat2}?overview=${overview}&alternatives=true`;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout

        const res = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);

        if (!res.ok) return null;

        const data: OSRMResponse = await res.json();

        if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
            // Seleciona sempre a rota de menor tempo
            const route = data.routes.reduce((best, current) => {
                return current.duration < best.duration ? current : best;
            }, data.routes[0]);
            const result = {
                distanceKm: route.distance / 1000,
                durationMins: route.duration / 60,
                ...(includeGeometry && route.geometry ? { geometry: route.geometry } : {})
            };

            // 3. Cache Success (only cache essential data, not geometry)
            try {
                const cacheData = {
                    distanceKm: result.distanceKm,
                    durationMins: result.durationMins
                };
                sessionStorage.setItem(key, JSON.stringify(cacheData));
            } catch (e) { /* Storage Full */ }

            return result;
        }
    } catch (e) {
        console.warn("OSRM Routing failed:", e);
    }

    return null;
};

/**
 * v2026.02.12: Decode OSRM polyline to lat/lng array
 * OSRM returns polyline6 encoded coordinates
 */
export const decodePolyline = (encoded: string): [number, number][] => {
    const points: [number, number][] = [];
    let index = 0;
    let lat = 0;
    let lng = 0;

    while (index < encoded.length) {
        let shift = 0;
        let result = 0;
        let byte: number;

        do {
            byte = encoded.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);

        const dlat = (result & 1) !== 0 ? ~(result >> 1) : (result >> 1);
        lat += dlat;

        shift = 0;
        result = 0;

        do {
            byte = encoded.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);

        const dlng = (result & 1) !== 0 ? ~(result >> 1) : (result >> 1);
        lng += dlng;

        points.push([lat / 1e6, lng / 1e6]);
    }

    return points;
};
